import { io } from 'socket.io-client';

// Explicit construction only; no socket/core/assets/context/ROM work on import.
// Main owns server authentication, native execution and website qualification.
// Media receipts acknowledge successful local canvas presentation ONLY.
export const GBA_LINKED_PROTOCOL = 'gba-linked-room-v1';
export function createGbaLinkedTransport(options = {}) {
  const root = options.global || globalThis;
  const server = (options.serverURL || '').replace(/\/$/, '');
  const storageKey = options.storageKey || 'tavern-gba-linked-room-session';
  const setTimer = options.setTimer || setTimeout, clearTimer = options.clearTimer || clearTimeout;
  const now = options.now || Date.now;
  const limit = (value, fallback, min, max) => {
    const n = value === undefined ? fallback : value;
    if (!Number.isSafeInteger(n) || n < min || n > max) throw new Error('TRANSPORT_LIMIT');
    return n;
  };
  const requestMs = limit(options.requestMs, 10000, 1, 30000);
  const joinMs = limit(options.joinMs, 10000, 1, 30000);
  const uploadMs = limit(options.uploadMs, 120000, 1, 120000);
  const refreshMs = limit(options.refreshMs, 120000, 1, 120000);
  const assetMs = limit(options.assetMs, 10000, 1, 30000);
  const maxPending = limit(options.maxPending, 16, 1, 64);
  const maxRomBytes = limit(options.maxRomBytes, 32 * 1024 * 1024, 192, 32 * 1024 * 1024);
  const name = typeof options.name === 'string' ? options.name.slice(0, 120) : 'Guest';
  let storage;
  try { storage = options.storage === undefined ? root.sessionStorage : options.storage; } catch {}
  const validToken = token => typeof token === 'string' && /^[a-f0-9]{64}$/.test(token);
  let sessionToken = validToken(options.sessionToken) ? options.sessionToken : null;
  if (!sessionToken) { try { const token = storage?.getItem(storageKey); if (validToken(token)) sessionToken = token; } catch {} }
  const ownsSocket = options.ownsSocket === undefined ? !options.socket : options.ownsSocket === true;
  const socket = options.socket || (options.ioFactory || io)(server + '/gba-room', {
    autoConnect: false, forceNew: true, reconnection: true
  });
  if (!socket || !['on', 'off', 'emit', 'timeout'].every(k => typeof socket[k] === 'function')) throw new Error('TRANSPORT_SOCKET');
  const emptyRoom = Object.freeze({ generation: 0, revision: 0, phase: 'empty',
    members: Object.freeze([]), capabilities: Object.freeze({}) });
  let snapshot = Object.freeze({ id: null, lease: null, room: emptyRoom });
  let disposed = false, joined = false, connectionSerial = 0, connectionId = null, joinSerial = 0;
  let display = null, assetsPromise = null, lastError = null, inputIdentity = null, nextInput = 0;
  const pending = new Set(), ackFlights = new Set(), listeners = new Set(), assetCleanups = new Set(), audioClosures = new Set();
  const metrics = { reconnects: 0, staleCallbacks: 0, ignoredFrames: 0, rejectedFrames: 0,
    renderedFrames: 0, renderAcks: 0, pcmDropped: 0, mediaErrors: 0 };
  const number = n => Number.isSafeInteger(n) && n >= 0;
  const uint32 = n => Number.isInteger(n) && n > 0 && n <= 0xffffffff;
  const seat = n => Number.isInteger(n) && n >= 1 && n <= 4;
  const failure = code => Object.freeze({ ok: false, code });
  const live = () => !disposed && joined && socket.connected === true && socket.id === connectionId;
  const current = (serial, id) => live() && serial === connectionSerial && id === snapshot.id;
  const safeObserve = fn => { try { const result = fn(); result?.catch?.(() => {}); } catch {} };
  function publish() { for (const listener of listeners) safeObserve(() => listener(snapshot)); }
  function freeze(value) {
    if (value && typeof value === 'object') { for (const child of Object.values(value)) freeze(child); Object.freeze(value); }
    return value;
  }
  function envelope(raw) {
    if (!raw || typeof raw !== 'object' || !raw.room || !Array.isArray(raw.room.members)) throw new Error('ENVELOPE');
    // A finite JSON-shaped control envelope, no binary state or private save bytes.
    const text = JSON.stringify({ id: raw.id, lease: raw.lease, room: raw.room });
    if (text.length > 65536) throw new Error('ENVELOPE_LENGTH');
    const next = JSON.parse(text), r = next.room;
    if (typeof next.id !== 'string' || !next.id.length || next.id.length > 128 ||
        !number(r.generation) || r.generation < 1 || !number(r.revision) ||
        typeof r.phase !== 'string' || r.phase.length > 32 || r.members.length > 64 ||
        (r.protocol !== undefined && r.protocol !== GBA_LINKED_PROTOCOL)) throw new Error('ENVELOPE');
    const ids = new Set(), seats = new Set();
    for (const m of r.members) {
      if (!m || typeof m.id !== 'string' || !m.id.length || m.id.length > 128 || ids.has(m.id) ||
          typeof m.name !== 'string' || m.name.length > 120 ||
          (m.seat !== null && m.seat !== undefined && (!seat(m.seat) || seats.has(m.seat)))) throw new Error('MEMBER');
      ids.add(m.id); if (m.seat) seats.add(m.seat);
    }
    if (!ids.has(next.id)) throw new Error('PRIVATE_IDENTITY');
    if (!r.capabilities || typeof r.capabilities !== 'object' || Array.isArray(r.capabilities)) throw new Error('CAPABILITIES');
    const l = next.lease;
    if (l != null && (!validToken(l.token) || !seat(l.seat) || l.generation !== r.generation ||
        !number(l.expiresAt) || r.members.find(m => m.id === next.id).seat !== l.seat)) throw new Error('PRIVATE_LEASE');
    return freeze(next);
  }
  function accept(raw, joining = false) {
    const next = envelope(raw);
    if (!joining && next.id !== snapshot.id) return false;
    const previous = snapshot;
    if (!joining && (next.room.generation < previous.room.generation ||
        (next.room.generation === previous.room.generation && next.room.revision < previous.room.revision))) return false;
    // Same-revision replies must not roll back a renewed private lease.
    let accepted = next;
    if (!joining && next.room.generation === previous.room.generation &&
        next.room.revision === previous.room.revision && next.lease && previous.lease &&
        next.lease.token === previous.lease.token && next.lease.expiresAt < previous.lease.expiresAt) {
      accepted = Object.freeze({ ...next, lease: previous.lease });
    }
    snapshot = accepted;
    if (display && !eligible(display)) retireMedia(display);
    if (display && eligible(display)) syncAudioPhase(display);
    publish();
    if (display && eligible(display) && !display.binding &&
        display.lastRevision !== snapshot.room.revision) requestMedia(display);
    return true;
  }
  function settleAll(code) { for (const p of [...pending]) p.finish(failure(code)); }
  function emitAck(event, payload, ms, callback) {
    if (ackFlights.size >= maxPending) { callback(failure('RATE_LIMIT')); return; }
    const flight = {}; ackFlights.add(flight);
    try {
      // Socket.IO timeout removes its registered callback. Caller cancellation
      // retains this bounded credit until SDK receipt/timeout, no abort-spam leak.
      socket.timeout(ms).emit(event, payload, (error, ack) => {
        if (!ackFlights.delete(flight)) { metrics.staleCallbacks++; return; }
        callback(error ? failure('TIMEOUT') : ack);
      });
    } catch (error) { ackFlights.delete(flight); throw error; }
  }
  function bounded(ms, signal, begin) {
    if (signal?.aborted) return Promise.resolve(failure('CANCELLED'));
    if (pending.size >= maxPending) return Promise.resolve(failure('RATE_LIMIT'));
    return new Promise(resolve => {
      let timer, done = false;
      const p = { active: () => !done, finish(result) {
        if (done) return; done = true; clearTimer(timer); pending.delete(p);
        signal?.removeEventListener('abort', abort); try { p.cancel?.(); } catch {} resolve(result);
      } };
      const abort = () => p.finish(failure('CANCELLED'));
      pending.add(p); signal?.addEventListener('abort', abort, { once: true });
      timer = setTimer(() => p.finish(failure('TIMEOUT')), ms);
      try { begin(p); } catch { p.finish(failure('UNAVAILABLE')); }
    });
  }
  function disconnect() {
    connectionSerial++; joinSerial++; joined = false; connectionId = null;
    settleAll('STALE_OPERATION');
    ackFlights.clear(); // Socket.IO fences old registered ACKs on disconnect.
    if (display) retireMedia(display);
    snapshot = Object.freeze({ id: null, lease: null, room: snapshot.room }); publish();
  }
  function connect() {
    if (disposed) return;
    disconnect(); connectionId = socket.id;
    if (typeof connectionId !== 'string' || !connectionId.length || socket.connected !== true) return;
    metrics.reconnects++;
    join(false);
  }
  function join(fresh) {
    const serial = connectionSerial, attempt = ++joinSerial, sid = connectionId;
    const isCurrent = () => !disposed && connectionSerial === serial && joinSerial === attempt &&
      socket.connected === true && socket.id === sid;
    const token = fresh ? null : sessionToken;
    void bounded(joinMs, null, p => {
      emitAck('gba_join', { name, sessionToken: token, protocol: GBA_LINKED_PROTOCOL }, joinMs, ack => {
        if (!p.active()) { metrics.staleCallbacks++; return; }
        if (!isCurrent()) { metrics.staleCallbacks++; return; }
        if (ack?.ok !== true) {
          p.finish(failure(typeof ack?.code === 'string' ? ack.code : 'UNAVAILABLE'));
          if (!fresh && ack?.code === 'INVALID_SESSION') { sessionToken = null; join(true); }
          else lastError = ack?.code || 'UNAVAILABLE';
          return;
        }
        try {
          if (!validToken(ack.sessionToken)) throw new Error('SESSION');
          // Validate before changing token, admission or snapshot.
          envelope(ack); joined = true; sessionToken = ack.sessionToken;
          try { storage?.setItem(storageKey, sessionToken); } catch {}
          accept(ack, true); lastError = null; p.finish({ ok: true });
          if (display && eligible(display)) requestMedia(display);
        } catch { joined = false; p.finish(failure('INVALID_RESPONSE')); lastError = 'INVALID_RESPONSE'; }
      });
    }).then(result => { if (isCurrent() && !result.ok) lastError = result.code; });
  }
  function request(payload, { signal } = {}) {
    if (!live()) return Promise.resolve(failure('UNAVAILABLE'));
    if (!payload || typeof payload !== 'object' || Array.isArray(payload) ||
        typeof payload.action !== 'string' || payload.action.length > 32 ||
        payload.generation !== snapshot.room.generation) return Promise.resolve(failure('STALE_GENERATION'));
    let wire;
    try { const text = JSON.stringify(payload); if (text.length > 4096) return Promise.resolve(failure('PAYLOAD')); wire = JSON.parse(text); }
    catch { return Promise.resolve(failure('PAYLOAD')); }
    const serial = connectionSerial, id = snapshot.id;
    const duration = wire.action === 'input' ? Math.min(requestMs, 3000) :
      ['refresh','override','topology','named_save','named_load'].includes(wire.action) ? refreshMs : wire.action === 'upload' ? uploadMs : requestMs;
    return bounded(duration, signal, p => {
      if (!current(serial, id)) { p.finish(failure('STALE_OPERATION')); return; }
      emitAck(wire.action === 'media' ? 'gba_media' : 'gba_request', wire,
        duration, ack => {
        if (!p.active()) { metrics.staleCallbacks++; return; }
        if (!current(serial, id)) { metrics.staleCallbacks++; p.finish(failure('STALE_OPERATION')); return; }
        if (!ack || typeof ack.ok !== 'boolean') { p.finish(failure('INVALID_RESPONSE')); return; }
        try {
          if (ack.room && !accept(ack)) { p.finish(failure('STALE_OPERATION')); return; }
          p.finish(ack);
        } catch { p.finish(failure('INVALID_RESPONSE')); }
      });
    });
  }
  async function readReply(response, controller) {
    const length = response.headers?.get?.('content-length');
    if (length !== null && length !== undefined && (!/^\d+$/.test(length) || Number(length) > 65536)) throw new Error('RESPONSE_LENGTH');
    if (!response.body?.getReader) throw new Error('RESPONSE_STREAM_REQUIRED');
    const reader = response.body.getReader(); let size = 0; const chunks = [];
    try {
      for (;;) {
        const { done, value } = await reader.read(); if (done) break;
        if (!(value instanceof Uint8Array)) throw new Error('RESPONSE_BYTES');
        size += value.byteLength; if (size > 65536 || chunks.length >= 4096) throw new Error('RESPONSE_LENGTH');
        chunks.push(value);
      }
      const bytes = new Uint8Array(size); let offset = 0;
      for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
      return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
    } catch (error) { controller.abort(); throw error; }
    finally { try { await reader.cancel(); } catch {} try { reader.releaseLock(); } catch {} }
  }
  function upload(file, { generation, signal } = {}) {
    if (!live()) return Promise.resolve(failure('UNAVAILABLE'));
    if (generation !== snapshot.room.generation) return Promise.resolve(failure('STALE_GENERATION'));
    if (!file || !Number.isSafeInteger(file.size) || file.size < 192 || file.size > maxRomBytes ||
        typeof file.name !== 'string' || !file.name.length || file.name.length > 120) return Promise.resolve(failure('ROM_SIZE'));
    const serial = connectionSerial, id = snapshot.id, token = sessionToken;
    return bounded(uploadMs, signal, p => {
      const controller = new AbortController(); p.cancel = () => controller.abort();
      const fetcher = options.fetcher || root.fetch.bind(root);
      let romName; try { romName = encodeURIComponent(file.name); } catch { p.finish(failure('ROM_NAME')); return; }
      Promise.resolve(fetcher(server + '/gba-room/rom', { method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/octet-stream', 'X-Gba-Session': token,
          'X-Rom-Name': romName, 'X-Room-Generation': String(generation) }, body: file }))
        .then(response => {
          if (!p.active()) { controller.abort(); throw new Error('STALE_OPERATION'); }
          return readReply(response, controller);
        }).then(ack => {
          if (!p.active()) { metrics.staleCallbacks++; return; }
          if (!current(serial, id) || sessionToken !== token) { metrics.staleCallbacks++; p.finish(failure('STALE_OPERATION')); return; }
          if (!ack || typeof ack.ok !== 'boolean') { p.finish(failure('INVALID_RESPONSE')); return; }
          if (ack.room && !accept(ack)) { p.finish(failure('STALE_OPERATION')); return; }
          p.finish(ack);
        }).catch(() => p.finish(failure(controller.signal.aborted ? 'CANCELLED' : 'UNAVAILABLE')));
    });
  }
  const assetRoot = server + '/gba-room/assets';
  const workletURL = options.workletURL || assetRoot + '/gba-room-audio-worklet.js';
  const document = options.document === undefined ? root.document : options.document;
  function getMedia() { return options.media || { pixels: root.TavernGBAPixels, viewer: root.TavernGBACanvasViewer, audio: root.TavernRoomAudio }; }
  function loadScript(key) {
    if (getMedia()[key]) return Promise.resolve();
    return new Promise((resolve, reject) => {
      if (!document?.createElement || !document.head?.appendChild) { reject(new Error('ASSET_DOCUMENT')); return; }
      const script = document.createElement('script'); script.async = true;
      script.src = assetRoot + '/' + ({ pixels: 'pixels.js', viewer: 'viewer.js', audio: 'audio.js' })[key];
      let timer, done = false;
      const cleanup = () => { clearTimer(timer); script.onload = null; script.onerror = null; script.remove?.(); assetCleanups.delete(cancel); };
      const finish = error => { if (done) return; done = true; cleanup(); if (error) reject(error); else resolve(); };
      const cancel = () => finish(new Error('ASSET_DISPOSED')); assetCleanups.add(cancel);
      timer = setTimer(() => finish(new Error('ASSET_TIMEOUT')), assetMs);
      script.onload = () => finish(getMedia()[key] ? null : new Error('ASSET_NAMESPACE'));
      script.onerror = () => finish(new Error('ASSET_LOAD'));
      document.head.appendChild(script);
    });
  }
  function assets() {
    if (!assetsPromise) {
      const loader = options.loadAssets ? Promise.resolve().then(() => options.loadAssets()) :
        loadScript('pixels').then(() => loadScript('viewer')).then(() => loadScript('audio'));
      assetsPromise = loader.then(() => {
        const m = getMedia(); if (!m.pixels?.createDecoder || !m.viewer?.create || !m.audio?.createPlayer) throw new Error('MEDIA_NAMESPACE');
        return m;
      });
      assetsPromise.catch(() => {});
    }
    return assetsPromise;
  }
  function eligible(record) {
    if (!live() || display !== record || record.closed || record.memberId !== snapshot.id ||
        record.generation !== snapshot.room.generation) return false;
    const member = snapshot.room.members.find(m => m.id === snapshot.id);
    return member?.connected !== false && (member?.seat ? member.seat === record.seat : record.seat === 1);
  }
  function retireMedia(record) {
    record.resizeObserver?.disconnect();record.resizeObserver=null;
    root.removeEventListener?.('resize',record.resize);record.resize=null;
    record.binding = null; record.latest = null;
    try { record.viewer?.close(); } catch {} record.viewer = null;
    const audio = record.audio; record.audio = null; record.phaseMuted = null;
    if (audio) {
      let closed;
      try { closed = Promise.resolve(audio.dispose()); } catch { closed = Promise.resolve(); }
      // Creation below is capped while previous contexts are still closing.
      audioClosures.add(closed);
      closed.catch(() => {}).finally(() => audioClosures.delete(closed));
    }
  }
  function closeDisplay(record) {
    if (record.closed) return; record.closed = true; retireMedia(record);
    if (display === record) display = null;
  }
  function requestMedia(record, force = false) {
    if (!eligible(record)) return;
    if (record.mediaPending) {
      record.mediaAgain = record.mediaAgain || force;
      record.mediaRetry = record.mediaRetry || (!record.binding && record.lastRevision !== snapshot.room.revision);
      return;
    }
    const serial = connectionSerial; record.mediaPending = true; record.lastRevision = snapshot.room.revision;
    void request({ action: 'media', generation: record.generation, seat: record.seat }).then(ack => {
      if (!eligible(record) || serial !== connectionSerial) return;
      if (!ack.ok) { metrics.mediaErrors++; lastError = ack.code; }
    }).finally(() => {
      record.mediaPending = false;
      const forced = record.mediaAgain, retry = record.mediaRetry;
      record.mediaAgain = false; record.mediaRetry = false;
      if (eligible(record) && (forced || (retry && !record.binding))) requestMedia(record, forced);
    });
  }
  function attachDisplay(canvas, { seat: selected, generation, memberId }) {
    if (disposed || !canvas || typeof canvas.getContext !== 'function' || !seat(selected) || !number(generation) ||
        generation < 1 || typeof memberId !== 'string') throw new Error('DISPLAY_BINDING');
    if (display) closeDisplay(display);
    const record = { canvas, seat: selected, generation, memberId, closed: false, binding: null,
      viewer: null, audio: null, phaseMuted: null, latest: null,
      mediaPending: false, mediaAgain: false, mediaRetry: false, lastRevision: -1 };
    display = record;
    // One bounded retained packet, no personal media queue.
    void assets().then(m => {
      if (display !== record || record.closed || disposed) return;
      record.assets = m; if (record.binding && eligible(record)) installBinding(record);
    }).catch(() => { if (display === record && !record.closed) { metrics.mediaErrors++; lastError = 'MEDIA_UNAVAILABLE'; } });
    if (eligible(record)) requestMedia(record);
    return () => closeDisplay(record);
  }
  function syncAudioPhase(record) {
    if (!record.audio) return;
    const muted = snapshot.room.phase !== 'running';
    if (record.phaseMuted === muted) return;
    record.phaseMuted = muted;
    // Personal presentation only. Frozen player setMuted flushes its finite ring;
    // no context recreation, resume bypass, request, native command or barrier.
    safeObserve(() => record.audio.setMuted(muted));
  }
  function reportHealth(record) {
    if (!eligible(record) || !record.binding) return;
    safeObserve(() => {
      const at = now();
      if (!number(at) || (record.healthAt !== undefined && (at < record.healthAt || at-record.healthAt < 5000))) return;
      const a = record.audioStatus, w = a?.worklet;
      const row = { generation:record.generation, streamEpoch:record.binding.streamEpoch,
        runtimeGeneration:record.binding.runtimeGeneration, seat:record.binding.seat,
        renderedFrames:metrics.renderedFrames, pcmDropped:metrics.pcmDropped,
        nativeFrame:record.nativeFrame || 0, contextRunning:Number(a?.contextState === 'running'),
        visible:Number(document?.visibilityState !== 'hidden') };
      for (const key of ['postedBlocks','creditDroppedBlocks','workletRejectedBlocks']) if(number(a?.[key])) row[key]=a[key];
      for (const key of ['underrunOutputFrames','renderedOutputFrames','bufferedFrames','overflowFrames','sequenceGapBlocks','cursorGapFrames']) if(number(w?.[key])) row[key]=w[key];
      const outputRate=a?.pageClock?.outputRate;if(number(outputRate))row.outputRate=outputRate;
      const timing=[w?.timing,a?.timingFeedback].filter(t=>t?.schemaVersion===1).sort((a,b)=>(b.lastRenderEnd||0)-(a.lastRenderEnd||0))[0];
      if(timing){
        for(const [key,source]of [['warmedOutputFrames','outputFrames'],['warmedUnderrunFrames','underrunOutputFrames'],['warmedInputFrames','acceptedInputFrames']])
          if(number(timing.afterWarmup?.[source]))row[key]=timing.afterWarmup[source];
        for(const key of ['sourceRate','clockInvalidMeasurements','renderClockGapFrames'])if(number(timing[key]))row[key]=timing[key];
        row.audioTimingValid=Number(timing.timingQualified===true);
      }
      for(const [key,value,scale]of [['receivedRateMilli',timing?.segmentReceivedRate,1000],['contextWallRatioMilli',a?.pageClock?.contextVsWallRatio,1000],
        ['receiptSpanMaxUs',a?.pageClock?.receiptSpanMaxMs,1000],['baseLatencyUs',a?.pageClock?.baseLatencyMs,1000],['outputLatencyUs',a?.pageClock?.outputLatencyMs,1000]]){
        const n=Math.round(value*scale);if(typeof value==='number'&&Number.isFinite(value)&&number(n)&&n<=1e12)row[key]=n;
      }
      record.healthAt=at;socket.emit('gba_health',row);
    });
  }
  function installBinding(record) {
    if (!record.assets || !record.binding || !eligible(record)) return;
    const bound = record.binding;
    try {
      record.viewer = record.assets.viewer.create({ canvas: record.canvas, seat: bound.seat, epoch: bound.streamEpoch,
        requestKeyframe: () => { if (record.binding === bound && eligible(record)) requestMedia(record, true); } });
      record.resize=()=>{
        if(!eligible(record)||!record.viewer?.resize)return;
        const parent=record.canvas.parentElement;if(!parent)return;
        const width=parent.getBoundingClientRect().width;
        const controls=parent.querySelector?.('.netplay-controls')?.getBoundingClientRect().height||0;
        const height=Math.max(160,(root.innerHeight||800)-record.canvas.getBoundingClientRect().top-controls-58);
        if(width>0)record.viewer.resize(width,height);
      };
      record.resize();root.addEventListener?.('resize',record.resize);
      if(root.ResizeObserver&&record.canvas.parentElement){record.resizeObserver=new root.ResizeObserver(record.resize);record.resizeObserver.observe(record.canvas.parentElement);}
    } catch { metrics.mediaErrors++; lastError = 'CANVAS_UNAVAILABLE'; return; }
    try {
      if (audioClosures.size >= 16) throw new Error('AUDIO_RETIREMENT_CAP');
      record.audio = record.assets.audio.createPlayer({ seat: bound.seat, runtimeGeneration: bound.runtimeGeneration, workletURL,
        timingFeedback:true, onStatus:status=>{if(record.binding!==bound||!eligible(record))return;record.audioStatus=status;reportHealth(record);} });
      syncAudioPhase(record);
      if (document?.addEventListener) record.audio.bindGesture(document);
      // prepare failure remains personal audio-only; it never blocks pixels/input.
      safeObserve(() => record.audio.prepare());
    } catch { record.audio = null; metrics.pcmDropped++; }
    const latest = record.latest; record.latest = null;
    if (latest) frame(latest);
  }
  function mediaBinding(row) {
    const record = display;
    if (!record || !eligible(record) || !row || row.seat !== record.seat - 1 || !uint32(row.streamEpoch) ||
        !number(row.runtimeGeneration) || row.runtimeGeneration < 1) return;
    if (record.binding && row.streamEpoch <= record.binding.streamEpoch) return;
    retireMedia(record);
    record.healthAt=undefined;record.audioStatus=null;record.nativeFrame=0;
    record.binding = Object.freeze({ seat: row.seat, streamEpoch: row.streamEpoch, runtimeGeneration: row.runtimeGeneration });
    installBinding(record);
  }
  function binary(value, max) {
    const buffer = ArrayBuffer.isView(value) ? value.buffer : value;
    if (Object.prototype.toString.call(buffer) !== '[object ArrayBuffer]') throw new Error('MEDIA_BYTES');
    const length = ArrayBuffer.isView(value) ? value.byteLength : buffer.byteLength;
    if (length < 1 || length > max) throw new Error('MEDIA_LENGTH');
    return ArrayBuffer.isView(value) ? new Uint8Array(buffer, value.byteOffset, length) : new Uint8Array(buffer);
  }
  function frame(row) {
    const record = display, b = record?.binding;
    if (!record || !b || !eligible(record) || !row || row.seat !== b.seat || row.streamEpoch !== b.streamEpoch || !number(row.nativeFrame)) {
      metrics.ignoredFrames++; return;
    }
    let packet;
    try { packet = binary(row.packet, 76832); } catch { metrics.rejectedFrames++; requestMedia(record, true); return; }
    if (!record.viewer) { record.latest = { ...row, packet: new Uint8Array(packet) }; return; }
    try {
      const receipt = record.viewer.present(packet);
      if (!receipt.accepted) { metrics.rejectedFrames++; return; }
      if (record.binding !== b || !eligible(record) || receipt.seat !== b.seat || receipt.epoch !== b.streamEpoch || !uint32(receipt.sequence)) return;
      metrics.renderedFrames++;
      record.nativeFrame=row.nativeFrame;
      // Not input ACK, not a native frame/cut assertion, no state ownership grant.
      socket.emit('gba_frame_ack', { streamEpoch: b.streamEpoch, sequence: receipt.sequence }); metrics.renderAcks++;
      reportHealth(record);
    } catch { metrics.rejectedFrames++; requestMedia(record, true); }
  }
  function pcm(row) {
    const record = display, b = record?.binding;
    if (!record || !b || !eligible(record) || !record.audio) { metrics.pcmDropped++; return; }
    if (row && row.packet !== undefined && ((row.seat !== undefined && row.seat !== b.seat) ||
        (row.runtimeGeneration !== undefined && row.runtimeGeneration !== b.runtimeGeneration))) { metrics.pcmDropped++; return; }
    try { if (!record.audio.push(binary(row?.packet === undefined ? row : row.packet, 3248))) metrics.pcmDropped++; }
    catch { metrics.pcmDropped++; } // audio rejection never poisons personal pixels/controls
  }
  function state(row) { if (!live()) return; try { if (!accept(row)) metrics.staleCallbacks++; } catch { lastError = 'INVALID_RESPONSE'; } }
  const handlers = { connect, disconnect, connect_error: disconnect, gba_state: state,
    gba_media_binding: mediaBinding, gba_frame: frame, [options.pcmEvent || 'gba_pcm']: pcm };
  for (const [event, fn] of Object.entries(handlers)) socket.on(event, fn);
  if (socket.connected) connect(); else if (ownsSocket) socket.connect?.();
  function subscribe(listener) {
    if (disposed || typeof listener !== 'function' || listeners.size >= 64) throw new Error('TRANSPORT_SUBSCRIBE');
    listeners.add(listener); safeObserve(() => listener(snapshot)); return () => listeners.delete(listener);
  }
  async function dispose() {
    if (disposed) return; disposed = true;
    connectionSerial++; joinSerial++; joined = false; settleAll('CANCELLED'); ackFlights.clear();
    if (display) closeDisplay(display);
    for (const cancel of [...assetCleanups]) cancel();
    listeners.clear(); for (const [event, fn] of Object.entries(handlers)) socket.off(event, fn);
    if (ownsSocket) socket.disconnect?.();
    // These are local AudioContext closes, NOT native cleanup receipts. Timeout
    // records incomplete local retirement instead of claiming successful closure.
    if (audioClosures.size) {
      let timer;
      const completed = await Promise.race([
        Promise.allSettled([...audioClosures]).then(() => true),
        new Promise(resolve => { timer = setTimer(() => resolve(false), 2000); })
      ]);
      clearTimer(timer);
      if (!completed) lastError = 'AUDIO_RETIREMENT_INCOMPLETE';
    }
  }
  function activateAudio(event) {
    // React callers may pass SyntheticEvent; only its original native event is
    // given to the player's isTrusted gate. No event is fabricated or retained.
    // With no argument, bindGesture(document)'s real capture listener performs
    // activation independently; this helper cannot manufacture gesture authority.
    return display?.audio?.resumeFromGesture(event?.nativeEvent || event) || Promise.resolve(false);
  }
  function nextInputSequence(){
    const key=snapshot.id+'|'+snapshot.room.generation+'|'+(snapshot.lease?.token||'');
    if(key!==inputIdentity){inputIdentity=key;nextInput=0;}
    if(nextInput>=Number.MAX_SAFE_INTEGER)throw new Error('INPUT_SEQUENCE_EXHAUSTED');
    return nextInput++;
  }
  return Object.freeze({ request, upload, getSnapshot: () => snapshot, subscribe, attachDisplay,nextInputSequence,
    activateAudio, dispose,
    getStatus: () => Object.freeze({ joined: live(), disposed, error: lastError, pending: pending.size, sdkPending: ackFlights.size,
      ...metrics,nativeFrame:display?.nativeFrame||0, media: display?.binding || null, audio: display?.audio?.snapshot?.() || null,
      websiteValidated: false }) });
}
export default createGbaLinkedTransport;
